// Pure geo math shared by the search radius control (task-032) and preview
// distance ordering (task-031's PreviewDataSource). Kept dependency-free and
// tiny — this stays in the initial bundle (F-030 budget).

import type { Bbox, SearchCenter } from "../data/types";

const EARTH_RADIUS_M = 6_371_000;
/** 512px tiles (MapLibre's projection): the world spans `TILE_SIZE * 2^zoom` CSS px. */
const TILE_SIZE = 512;
const MAX_MERCATOR_LAT = 85.051_128_78;

// --- Result-fit constants (F-039). Deliberately the SAME numbers as the server's
// LocatorDefaultView.fitTo, so the camera a search settles on and the camera a locator opens
// on read as one behaviour rather than two competing conventions. Because that is a hand-mirrored
// copy in another language, the two are pinned together by test/fixtures/fit-cases.json — a golden
// set both sides assert against, so a change here fails the Kotlin test until it is matched.
/** 25% breathing room so pins never sit flush against the map edge. */
const FIT_PADDING = 1.25;
/** A zero-extent result (one location, or several at identical coordinates) settles here — a
 * neighbourhood view, never MapLibre's maximum zoom staring at a rooftop. */
const SINGLE_RESULT_ZOOM = 13;
/** Widest sensible zoom-out: below this the map is a globe and frames nothing useful. */
const MIN_FIT_ZOOM = 2;
/** Tightest zoom a fit will choose, so a dense cluster of results still shows its surroundings. */
const MAX_FIT_ZOOM = 16;

/** Forward spherical-mercator: latitude -> normalized Y in [0, 1] (0 = north edge). */
function latToMercatorY(lat: number): number {
  const clamped = Math.max(-MAX_MERCATOR_LAT, Math.min(MAX_MERCATOR_LAT, lat));
  const rad = (clamped * Math.PI) / 180;
  return (1 - Math.log(Math.tan(rad) + 1 / Math.cos(rad)) / Math.PI) / 2;
}

/** Inverse spherical-mercator: normalized Y -> latitude. */
function mercatorYToLat(y: number): number {
  const n = Math.PI * (1 - 2 * y);
  return (Math.atan(Math.sinh(n)) * 180) / Math.PI;
}

/**
 * The Web-Mercator bounding box a map of [widthPx]x[heightPx] CSS pixels would
 * show when centered on [center] at [zoom] — the same 512px-tile projection
 * MapLibre uses. Lets the component fetch the FIRST viewport keyed to the
 * locator's configured default view WITHOUT waiting for (or depending on the
 * async camera state of) the lazily-loaded map (task-036 TTI): the list becomes
 * interactive before MapLibre parses/compiles. It is an honest estimate of the
 * map's initial bounds — the map re-queries precisely on the first user
 * pan/zoom — so tiny sub-pixel divergence from MapLibre's own `getBounds()` is
 * immaterial to what the visitor sees.
 *
 * Latitude uses the spherical-mercator forward/inverse transform so the box is
 * correct away from the equator (a fixed degrees-per-pixel would skew north);
 * longitude is linear in mercator. Clamped to the projection's valid latitude
 * range (~±85.05°) and to [-180, 180] longitude.
 */
export function viewportBboxFor(center: SearchCenter, zoom: number, widthPx: number, heightPx: number): Bbox {
  const worldPx = TILE_SIZE * 2 ** zoom;
  const halfLngSpan = (360 * (widthPx / 2)) / worldPx;
  const centerY = latToMercatorY(center.lat);
  const halfYSpan = heightPx / 2 / worldPx;

  return {
    west: Math.max(-180, center.lng - halfLngSpan),
    south: mercatorYToLat(Math.min(1, centerY + halfYSpan)),
    east: Math.min(180, center.lng + halfLngSpan),
    north: mercatorYToLat(Math.max(0, centerY - halfYSpan)),
  };
}

/**
 * The integer-free zoom at which [bbox] exactly fits a [widthPx]x[heightPx]
 * viewport — the same result MapLibre's `fitBounds` computes (padding aside).
 * Used to derive a search's query zoom CLIENT-SIDE so a radius search produces
 * the correct clusters/pins WITHOUT depending on the (possibly not-yet-attached,
 * or mid-fitBounds-animation) map camera. Takes the tighter of the horizontal
 * and vertical fits so the whole box is visible; clamped to a sane zoom range.
 */
export function fitZoomForBbox(bbox: Bbox, widthPx: number, heightPx: number): number {
  const lngSpan = Math.max(1e-9, Math.abs(bbox.east - bbox.west));
  const ySpan = Math.max(1e-9, Math.abs(latToMercatorY(bbox.north) - latToMercatorY(bbox.south)));
  const zoomForWidth = Math.log2((360 * widthPx) / (TILE_SIZE * lngSpan));
  const zoomForHeight = Math.log2(heightPx / (TILE_SIZE * ySpan));
  return Math.max(0, Math.min(22, Math.min(zoomForWidth, zoomForHeight)));
}

/** A map camera: where it looks and how close. */
export interface CameraView {
  center: SearchCenter;
  zoom: number;
}

/**
 * The camera that FRAMES [bounds] in a [widthPx]x[heightPx] map pane — the F-039 primitive that
 * makes the map a consequence of the result list instead of an input to it. The server hands back
 * the extent of the whole result set (ViewportPayload.resultBounds); this turns it into the
 * center + zoom the visitor actually gets, using the embed's REAL canvas size (the server can only
 * assume one by default, so it errs wide — see LocatorDefaultView.fitTo, whose padding, clamps and
 * antimeridian handling this mirrors, enforced by the shared golden fixture
 * test/fixtures/fit-cases.json rather than by good intentions).
 *
 * CLAMPS ARE THE PRODUCT BEHAVIOUR, not defensive noise: a single result must settle at a
 * neighbourhood zoom rather than a rooftop, and a continent-wide spread must stop at a readable
 * zoom-out rather than a globe.
 *
 * ANTIMERIDIAN: a result set straddling ±180° yields min/max longitudes describing the box the
 * LONG way round (e.g. west=-179, east=179). Any span wider than half the globe is therefore
 * re-expressed the short way (east may exceed 180°, which MapLibre accepts), so results 2° apart
 * frame as 2° apart instead of collapsing the camera to a world view.
 */
export function fitViewToBounds(bounds: Bbox, widthPx: number, heightPx: number): CameraView {
  let west = bounds.west;
  let east = bounds.east;
  if (east - west > 180) {
    [west, east] = [east, west + 360];
  }

  const lngSpan = Math.abs(east - west) * FIT_PADDING;
  const ySpan = Math.abs(latToMercatorY(bounds.north) - latToMercatorY(bounds.south)) * FIT_PADDING;
  const degenerateLng = lngSpan < 1e-7;
  const degenerateLat = ySpan < 1e-9;

  let zoom: number;
  if (degenerateLng && degenerateLat) {
    zoom = SINGLE_RESULT_ZOOM;
  } else {
    const zoomForWidth = degenerateLng ? MAX_FIT_ZOOM : Math.log2((360 * widthPx) / (TILE_SIZE * lngSpan));
    const zoomForHeight = degenerateLat ? MAX_FIT_ZOOM : Math.log2(heightPx / (TILE_SIZE * ySpan));
    zoom = Math.min(zoomForWidth, zoomForHeight);
  }

  return {
    center: { lat: (bounds.south + bounds.north) / 2, lng: normalizeLng((west + east) / 2) },
    zoom: Math.max(MIN_FIT_ZOOM, Math.min(MAX_FIT_ZOOM, zoom)),
  };
}

/** Wraps a longitude back into [-180, 180) after antimeridian arithmetic. */
function normalizeLng(lng: number): number {
  return (((lng + 180) % 360) + 360) % 360 - 180;
}

/** Great-circle distance between two points; good enough for widget-scale distances. */
export function haversineMeters(a: SearchCenter, b: SearchCenter): number {
  const toRad = (deg: number): number => (deg * Math.PI) / 180;
  const dLat = toRad(b.lat - a.lat);
  const dLng = toRad(b.lng - a.lng);
  const lat1 = toRad(a.lat);
  const lat2 = toRad(b.lat);
  const h = Math.sin(dLat / 2) ** 2 + Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLng / 2) ** 2;
  return 2 * EARTH_RADIUS_M * Math.asin(Math.min(1, Math.sqrt(h)));
}

/**
 * A bounding box of [radiusMeters] around [center] (F-032 radius control). Used
 * to fit the map so the visible viewport covers the requested radius — the
 * server has no separate "radius" query param, it only knows bbox + zoom, so the
 * radius control works entirely by choosing what the client asks the map to show.
 *
 * Latitude degrees are a fixed distance everywhere; longitude degrees shrink
 * toward the poles, so the longitude delta is corrected by cos(latitude).
 * Clamped so an oversized radius near a pole can't produce an out-of-range bbox.
 */
export function bboxForRadius(center: SearchCenter, radiusMeters: number): Bbox {
  const METERS_PER_LAT_DEGREE = 111_320;
  const latDelta = radiusMeters / METERS_PER_LAT_DEGREE;
  const lngDenominator = METERS_PER_LAT_DEGREE * Math.cos((center.lat * Math.PI) / 180);
  // Near the poles cos(lat) -> 0; guard against a division blow-up by falling
  // back to the full longitude range rather than producing Infinity/NaN.
  const lngDelta = lngDenominator > 1 ? radiusMeters / lngDenominator : 180;

  return {
    west: Math.max(-180, center.lng - lngDelta),
    south: Math.max(-90, center.lat - latDelta),
    east: Math.min(180, center.lng + lngDelta),
    north: Math.min(90, center.lat + latDelta),
  };
}
